import { useCallback, useEffect, useState } from 'react';
import type { PlantillaMeta } from '../../api/ops/canales';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { diaMes, diaYHora } from '../../logica/fechasCortas';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Canales';
import {
    AYUDA_PLANTILLAS,
    AVISO_EJEMPLO,
    BOTON_BAJA,
    CALIDAD,
    CATEGORIA,
    DE_PLANTILLA,
    ESTADO_PLANTILLA,
    erroresPlantilla,
    extracto,
    FILTROS_PLANTILLAS,
    HISTORIAL_PLANTILLA,
    IDIOMA_PLANTILLA,
    nombreDe,
    nombreVariable,
    nombreVersionado,
    ORIGEN_PLANTILLA,
    partesDe,
    permitido,
    plantillaParaEnviar,
    siguienteLibre,
    trozos,
    VACIA_PLANTILLAS,
    VARIABLES,
    variablesDe,
    variablesLibres,
    type Borrador,
} from './reglas';

/**
 * 11.4 Plantillas de Meta: tabla (en móvil, tarjetas) con nombre, extracto, categoría (la pedida y la de Meta si cambia),
 * idioma, de quién es y número; filtro Todas · En revisión · Con problemas · Retiradas; ficha (`?plantilla=`) con
 * Contenido · Historial; «Nueva plantilla» solo de marketing con el bloque Mensajes (G448) y el botón de baja (D12),
 * «Corregir y enviar otra» desde una propia rechazada, y retirar una propia (`withdrawMetaTemplate`, también en solo
 * lectura). Las de Reservaria no se editan ni se retiran aquí: se cambian en Mensajes › Mensajes a clientes.
 */
export const RUTA_MENSAJES_A_CLIENTES = '/mensajes?apartado=plantillas';

/** La dirección web del negocio sin `https://` (G125). */
function sinProtocolo(url: string | null | undefined): string {
    return (url ?? '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const IDIOMAS = [
    { valor: 'es-ES', texto: 'Español' },
    { valor: 'en-GB', texto: 'Inglés' },
];

const borradorVacio = (canal: string): Borrador => ({ canal, nombre: '', idioma: 'es-ES', titulo: '', conTitulo: false, cuerpo: '', pie: '', conPie: false, ejemplos: {} });

export function PlantillasMeta(props: PropsApartado) {
    const { ops, negocio, movil, soloLectura, busqueda, navegar, ruta, datos, irA, membresia, nombreNegocio } = props;
    const enlace = sinProtocolo(membresia?.public_url);
    const { puede } = usePermisos();
    const gestiona = puede('canales.plantillas.gestionar');
    const propietario = membresia?.is_owner === true;
    const avisar = useAvisoBreve();
    const retraso = useEnvioRetrasado();
    const [plantillas, setPlantillas] = useState<PlantillaMeta[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [filtro, setFiltro] = useState(0);
    const [editor, setEditor] = useState<{ borrador: Borrador; corrige: PlantillaMeta | null } | null>(null);
    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    const numeros = datos.canales.filter((c) => c.channel_type === 'whatsapp' && (c.status === 'active' || c.status === 'degraded'));
    const hayWhatsApp = permitido('whatsapp', datos.bloques).ok && numeros.length > 0;
    const conMensajes = datos.bloques === null || datos.bloques.has('mensajes_campanas');
    const puedeNueva = gestiona && !soloLectura && conMensajes && hayWhatsApp;

    useEffect(() => {
        if (!hayWhatsApp) {
            return;
        }
        let vivo = true;
        const todas: PlantillaMeta[] = [];
        const pagina = (cursor: string | null, vueltas: number): Promise<void> =>
            ops.listMetaTemplates(negocio, { limit: 100, ...(cursor === null ? {} : { cursor }) }).then((r) => {
                todas.push(...r.data);
                return r.next_cursor != null && vueltas < 10 ? pagina(r.next_cursor, vueltas + 1) : undefined;
            });
        pagina(null, 0)
            .then(() => {
                if (vivo) {
                    setPlantillas(todas);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, hayWhatsApp]);

    const abrirNueva = () => {
        setEditor({ borrador: borradorVacio(numeros[0]?.id ?? ''), corrige: null });
    };
    useCabecera({ ayuda: AYUDA_PLANTILLAS, acciones: puedeNueva && plantillas !== null ? [{ clave: 'nueva', texto: 'Nueva plantilla', icono: 'plus', onClick: abrirNueva }] : [] });

    if (!hayWhatsApp) {
        return (
            <div className="rv-ca-apartado">
                <div className="rv-cn-primero">
                    <span className="rv-cn-primero__baldosa">
                        <Icono nombre="message-circle" />
                    </span>
                    <strong>Primero, un número de WhatsApp</strong>
                    <span>Las plantillas viven en la cuenta de WhatsApp de tu negocio en Meta. Conecta un número y aquí verás las de Reservaria y las tuyas.</span>
                    <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={() => { irA('canales'); }}>
                        Ir a Canales
                    </Boton>
                </div>
            </div>
        );
    }
    if (error !== null) {
        return <EstadoError seccion="las plantillas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (plantillas === null) {
        return <EstadoCargando seccion="las plantillas de Meta" />;
    }

    const base = ruta.split('?')[0] ?? ruta;
    const abrir = (id: string | null) => {
        navegar(id === null ? `${base}?apartado=plantillas` : `${base}?apartado=plantillas&plantilla=${encodeURIComponent(id)}`, true);
    };
    const f = FILTROS_PLANTILLAS[filtro] ?? FILTROS_PLANTILLAS[0];
    const visibles = plantillas.filter((p) => f.cumple(p));
    const numero = (id: string) => datos.canales.find((c) => c.id === id)?.display_identifier ?? '';
    const elegida = plantillas.find((p) => p.id === busqueda.get('plantilla')) ?? null;
    const [vacioTitulo, vacioTexto] = VACIA_PLANTILLAS[f.valor] ?? ['', ''];

    const enviar = (b: Borrador) => {
        const cuerpo = plantillaParaEnviar(b);
        setEditor(null);
        abrir(null);
        retraso.programar({
            texto: `«${cuerpo.name}» enviada a Meta. Te avisamos en la campana cuando responda.`,
            donde: 'lista',
            enviar: () => {
                ops.createMetaTemplate(negocio, cuerpo)
                    .then(() => { recargar(); })
                    .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido enviar a Meta.' }); });
            },
        });
    };

    return (
        <div className="rv-ca-apartado">
            <Filtros etiqueta="Plantillas de Meta" items={FILTROS_PLANTILLAS.map((x) => ({ etiqueta: x.etiqueta }))} valor={filtro} onCambio={setFiltro} />
            {retraso.pendiente !== null && retraso.pendiente.donde === 'lista' && <Deshacer texto={retraso.pendiente.texto} clave={retraso.pendiente.clave} icono="send" movil={movil} onDeshacer={() => { retraso.deshacer(); }} />}
            {soloLectura ? (
                <p className="rv-cn-nota-blanca" role="note">
                    <Icono nombre="eye" />
                    <span>El negocio está en solo lectura: puedes ver las plantillas y retirarlas, pero no enviar nuevas a Meta.</span>
                </p>
            ) : (
                !conMensajes && (
                    <div className="rv-cn-nota-blanca" role="note">
                        <Icono nombre="puzzle" />
                        <span>{`Las de Reservaria ya están. Para crear las tuyas (de marketing) necesitas el bloque Mensajes y campañas.${propietario ? '' : ' Pídeselo al propietario.'}`}</span>
                        {propietario && (
                            <Boton tipo="secundario" icono="plus" movil={movil} disabled>
                                Añadir a mi plan
                            </Boton>
                        )}
                    </div>
                )
            )}
            {visibles.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{vacioTitulo}</strong>
                    <span>{vacioTexto}</span>
                    {f.valor === 'todas' && puedeNueva && (
                        <Boton icono="plus" movil={movil} onClick={abrirNueva}>
                            Nueva plantilla
                        </Boton>
                    )}
                </div>
            ) : (
                <Tabla
                    etiqueta="Plantillas de Meta"
                    movil={movil}
                    columnas={[
                        { clave: 'plantilla', titulo: 'Plantilla', ancho: 'minmax(240px, 1.8fr)' },
                        { clave: 'categoria', titulo: 'Categoría', ancho: 'minmax(110px, 0.8fr)' },
                        { clave: 'idioma', titulo: 'Idioma', ancho: '84px' },
                        { clave: 'de', titulo: 'De', ancho: 'minmax(130px, 1fr)' },
                        { clave: 'estado', titulo: 'Estado', ancho: '112px' },
                    ]}
                    filas={visibles.map((p) => {
                        const cat = CATEGORIA[p.category ?? p.requested_category];
                        const cambiada = p.category != null && p.category !== p.requested_category;
                        const [estado, tono] = ESTADO_PLANTILLA[p.status];
                        return {
                            clave: p.id,
                            etiqueta: p.name,
                            onAbrir: () => { abrir(p.id); },
                            celdas: {
                                plantilla: (
                                    <span className="rv-en-celda">
                                        <strong>{p.name}</strong>
                                        <small className="rv-cn-extracto">{extracto(p)}</small>
                                    </span>
                                ),
                                categoria: (
                                    <span className="rv-en-celda">
                                        <strong>{cat + (p.category == null ? ' (pedida)' : '')}</strong>
                                        {cambiada && <small className="rv-cn-aviso-texto">{`Pediste ${CATEGORIA[p.requested_category].toLowerCase()}`}</small>}
                                    </span>
                                ),
                                idioma: IDIOMA_PLANTILLA[p.locale] ?? p.locale,
                                de: (
                                    <span className="rv-en-celda">
                                        <strong>{ORIGEN_PLANTILLA[p.requested_by] ?? p.requested_by}</strong>
                                        <small>{numero(p.channel_id)}</small>
                                    </span>
                                ),
                                estado: p.status === 'approved' ? '' : <Chip estado={tono}>{estado}</Chip>,
                            },
                        };
                    })}
                />
            )}
            {elegida !== null && editor === null && (
                <FichaPlantilla
                    key={elegida.id}
                    {...props}
                    plantilla={elegida}
                    numero={numero(elegida.channel_id)}
                    puedeCorregir={!soloLectura && conMensajes && gestiona}
                    onCerrar={() => { abrir(null); }}
                    onCorregir={(p) => {
                        const partes = partesDe(p);
                        setEditor({
                            corrige: p,
                            borrador: { canal: p.channel_id, nombre: nombreVersionado(p.name), idioma: p.locale, titulo: partes.titulo, conTitulo: partes.titulo !== '', cuerpo: partes.cuerpo, pie: partes.pie, conPie: partes.pie !== '', ejemplos: {} },
                        });
                    }}
                    onRetirada={(texto) => {
                        abrir(null);
                        avisar({ tipo: 'ok', texto });
                        recargar();
                    }}
                    irAMensajes={() => { navegar(RUTA_MENSAJES_A_CLIENTES); }}
                />
            )}
            {editor !== null && (
                <Editor
                    movil={movil}
                    inicial={editor.borrador}
                    corrige={editor.corrige}
                    numeros={numeros.map((c) => ({ valor: c.id, texto: `${nombreDe(c)} · ${c.display_identifier}` }))}
                    existentes={plantillas}
                    enlace={enlace}
                    nombreNegocio={nombreNegocio}
                    onCerrar={() => { setEditor(null); }}
                    onEnviar={enviar}
                />
            )}
        </div>
    );
}

function FichaPlantilla({
    ops,
    negocio,
    movil,
    plantilla,
    numero,
    puedeCorregir,
    nombreNegocio,
    membresia,
    onCerrar,
    onCorregir,
    onRetirada,
    irAMensajes,
}: PropsApartado & { plantilla: PlantillaMeta; numero: string; puedeCorregir: boolean; onCerrar: () => void; onCorregir: (p: PlantillaMeta) => void; onRetirada: (texto: string) => void; irAMensajes: () => void }) {
    const avisar = useAvisoBreve();
    const [pestana, setPestana] = useState(0);
    const [leida, setLeida] = useState<PlantillaMeta | null>(null);
    const [retirar, setRetirar] = useState(false);
    const [ocupado, setOcupado] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.getMetaTemplate(negocio, plantilla.id)
            .then((p) => {
                if (vivo) {
                    setLeida(p);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, plantilla.id]);

    const t = leida ?? plantilla;
    const propia = t.requested_by === 'panel';
    const [estado, tono] = ESTADO_PLANTILLA[t.status];
    const cat = CATEGORIA[t.category ?? t.requested_category];
    const partes = partesDe(t);
    const motivo = t.status_reason ?? '';
    const alerta =
        t.status === 'rejected'
            ? { clase: 'roja', icono: 'circle-x' as const, titulo: 'Meta la ha rechazado', texto: `${motivo}${propia ? ' Corrígela y envía otra: esta no se edita.' : t.requested_by === 'entregas' ? ' Mientras, sale el último texto aprobado. El texto se corrige en Mensajes › Mensajes a clientes.' : ' Mientras, sale el último texto aprobado.'}`.trim() }
            : t.status === 'paused'
              ? { clase: 'roja', icono: 'pause' as const, titulo: 'Meta la ha pausado', texto: `${motivo} No se usa hasta que Meta la reactive.`.trim() }
              : t.status === 'pending'
                ? { clase: 'amarilla', icono: 'hourglass' as const, titulo: 'En revisión de Meta', texto: 'Suele responder en minutos; a veces tarda hasta 24 horas. Te avisamos en la campana.' }
                : t.status === 'withdrawn'
                  ? { clase: 'gris', icono: 'archive' as const, titulo: 'Retirada', texto: 'Ya no está en tu cuenta de Meta y no se usa.' }
                  : null;
    const datos: { k: string; v: string; sub?: string }[] = [
        { k: 'Nombre en Meta', v: t.name },
        { k: 'Categoría', v: cat + (t.category == null ? ' (pedida)' : ''), ...(t.category != null && t.category !== t.requested_category ? { sub: `Meta la ha cambiado: pediste ${CATEGORIA[t.requested_category].toLowerCase()}` } : {}) },
        { k: 'Idioma', v: IDIOMA_PLANTILLA[t.locale] ?? t.locale },
        { k: 'Número', v: numero },
        { k: 'De', v: DE_PLANTILLA[t.requested_by] ?? t.requested_by },
        { k: 'Calidad en Meta', v: (CALIDAD[t.quality ?? 'unknown'] ?? ['Sin valorar'])[0] },
        { k: 'Enviada', v: diaMes(t.submitted_at) },
    ];
    const botones = [...partes.botones.filter((b) => b !== BOTON_BAJA), ...(t.requested_category === 'marketing' ? [BOTON_BAJA] : [])];

    const hacerRetirada = () => {
        setOcupado(true);
        ops.withdrawMetaTemplate(negocio, t.id)
            .then(() => {
                setRetirar(false);
                onRetirada(`«${t.name}» retirada de Meta.`);
            })
            .catch((e: unknown) => { avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido retirar.' }); })
            .finally(() => { setOcupado(false); });
    };

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={t.name}
                eyebrow={`Plantilla de Meta · ${propia ? 'tuya' : (ORIGEN_PLANTILLA[t.requested_by] ?? t.requested_by)}`}
                subtitulo={`${cat} · ${IDIOMA_PLANTILLA[t.locale] ?? t.locale}`}
                chip={estado}
                chipEstado={tono}
                icono="message-square-text"
                accion={propia && t.status === 'rejected' && puedeCorregir ? 'Corregir y enviar otra' : ''}
                accionIcono="pencil"
                onAccion={() => { onCorregir(t); }}
            >
                <div className="rv-cn-ficha">
                    <Segmentos etiqueta="Partes de la plantilla" movil={movil} items={[{ etiqueta: 'Contenido', icono: 'message-square-text' }, { etiqueta: 'Historial', icono: 'history' }]} valor={pestana} onCambio={setPestana} />
                    {pestana === 0 && (
                        <>
                            {alerta !== null && (
                                <div className={`rv-cn-alerta rv-cn-alerta--${alerta.clase}`} role="note">
                                    <Icono nombre={alerta.icono} />
                                    <span>
                                        <strong>{alerta.titulo}</strong>
                                        <span>{alerta.texto}</span>
                                    </span>
                                </div>
                            )}
                            <div className="rv-cn-dos">
                                <div>
                                    {datos.map((d) => (
                                        <div key={d.k} className="rv-ca-dato">
                                            <span>{d.k}</span>
                                            <span className="rv-cn-dato-valor">
                                                <strong>{d.v}</strong>
                                                {d.sub !== undefined && <small className="rv-cn-aviso-texto">{d.sub}</small>}
                                            </span>
                                        </div>
                                    ))}
                                    {(t.requested_by === 'entregas' || t.requested_by === 'platform') && (
                                        <div className="rv-cn-pie-ficha">
                                            <span>{t.requested_by === 'entregas' ? 'Es de servicio: la envía Reservaria a Meta y aquí no se edita. Se cambia en Mensajes › Mensajes a clientes; cada texto nuevo se envía a Meta solo.' : 'La prepara Reservaria y no se cambia.'}</span>
                                            {t.requested_by === 'entregas' && (
                                                <Boton tipo="secundario" icono="arrow-right" movil={movil} onClick={irAMensajes}>
                                                    Ir a Mensajes a clientes
                                                </Boton>
                                            )}
                                        </div>
                                    )}
                                    {propia && t.status !== 'withdrawn' && (
                                        <div className="rv-cn-pie-ficha">
                                            <Boton tipo="secundario" rojo icono="archive" movil={movil} onClick={() => { setRetirar(true); }}>
                                                Retirar de Meta
                                            </Boton>
                                            <span>Se borra de tu cuenta de WhatsApp en Meta y Campañas deja de usarla.</span>
                                        </div>
                                    )}
                                </div>
                                <VistaPrevia titulo={partes.titulo} cuerpo={partes.cuerpo} pie={partes.pie} botones={botones} ejemplos={{ enlace: sinProtocolo(membresia?.public_url) }} nombreNegocio={nombreNegocio} pie2="Con los ejemplos" />
                            </div>
                        </>
                    )}
                    {pestana === 1 && (
                        <>
                            <div className="rv-cn-incidencias">
                                {[...(t.history ?? [])].reverse().map((h) => {
                                    const [icono, titulo] = HISTORIAL_PLANTILLA[h.to_status];
                                    const [chip, tonoH] = ESTADO_PLANTILLA[h.to_status];
                                    return (
                                        <div key={`${h.to_status}-${h.changed_at}`} className="rv-cn-incidencia">
                                            <span className={`rv-cn-baldosa rv-cn-baldosa--${tonoH === 'problem' ? 'roja' : tonoH === 'confirmed' ? 'negra' : 'gris'}`}>
                                                <Icono nombre={icono} />
                                            </span>
                                            <span className="rv-cn-incidencia__texto">
                                                <span className="rv-cn-linea">
                                                    <strong>{titulo}</strong>
                                                    <Chip estado={tonoH}>{chip}</Chip>
                                                </span>
                                                {h.reason != null && h.reason !== '' && <span>{h.reason}</span>}
                                                <small>{diaYHora(h.changed_at)}</small>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre="lock" />
                                <span>Una plantilla no se edita: un texto nuevo es otra plantilla. Mientras Meta no la aprueba, se sigue usando la última aprobada.</span>
                            </p>
                        </>
                    )}
                </div>
            </PanelLateral>
            <Ventana
                abierta={retirar}
                titulo={`¿Retirar «${t.name}» de Meta?`}
                texto="Se borra de tu cuenta de WhatsApp en Meta y Campañas deja de poder usarla. No se puede deshacer: para volver a usarla habría que enviarla otra vez a aprobación."
                accion="Retirar"
                accionIcono="archive"
                ocupado={ocupado}
                ocupadoTexto="Retirando…"
                movil={movil}
                onAccion={hacerRetirada}
                onCerrar={() => { if (!ocupado) { setRetirar(false); } }}
            />
        </>
    );
}

function VistaPrevia({ titulo, cuerpo, pie, botones, ejemplos, nombreNegocio, pie2, vacio }: { titulo: string; cuerpo: string; pie: string; botones: string[]; ejemplos: Record<string, string>; nombreNegocio: string; pie2: string; vacio?: string }) {
    const iniciales = nombreNegocio
        .split(/\s+/)
        .filter((p) => p !== '')
        .slice(0, 2)
        .map((p) => p.charAt(0).toLocaleUpperCase('es'))
        .join('');
    return (
        <div className="rv-cn-vista" aria-live="polite">
            <div className="rv-cn-vista__cab">
                <span>Vista previa</span>
                <span>{pie2}</span>
            </div>
            <div className="rv-cn-movil">
                <div className="rv-cn-movil__cab">
                    <span className="rv-cn-movil__avatar">{iniciales}</span>
                    <span>
                        <strong>{nombreNegocio}</strong>
                        <small>
                            <Icono nombre="message-circle" />
                            WhatsApp
                        </small>
                    </span>
                </div>
                <div className="rv-cn-movil__chat">
                    <span className="rv-cn-movil__dia">Hoy</span>
                    <div className="rv-cn-burbuja">
                        {titulo !== '' && <strong>{titulo}</strong>}
                        <p>{cuerpo.trim() === '' ? <span className="rv-cn-burbuja__vacio">{vacio}</span> : trozos(cuerpo, ejemplos).map((s, i) => (s.variable ? <mark key={i}>{s.texto}</mark> : <span key={i}>{s.texto}</span>))}</p>
                        {pie !== '' && <small>{pie}</small>}
                    </div>
                    {botones.map((b) => (
                        <div key={b} className="rv-cn-boton-chat">
                            <Icono nombre={b === BOTON_BAJA ? 'user-x' : 'copy'} />
                            {b}
                        </div>
                    ))}
                </div>
            </div>
            <span className="rv-cn-leyenda">
                <i />
                Variable, con su ejemplo
            </span>
        </div>
    );
}

function Editor({
    movil,
    inicial,
    corrige,
    numeros,
    existentes,
    enlace,
    nombreNegocio,
    onCerrar,
    onEnviar,
}: {
    movil: boolean;
    inicial: Borrador;
    corrige: PlantillaMeta | null;
    numeros: { valor: string; texto: string }[];
    existentes: readonly PlantillaMeta[];
    enlace: string;
    nombreNegocio: string;
    onCerrar: () => void;
    onEnviar: (b: Borrador) => void;
}) {
    const [b, setB] = useState<Borrador>(inicial);
    const [intentado, setIntentado] = useState(false);
    const errores = intentado ? erroresPlantilla(b, existentes) : {};
    const usadas = variablesDe(b.cuerpo);
    // G722: solo las variables libres piden ejemplo; las del catálogo llevan el que pone Reservaria.
    const libres = variablesLibres(b.cuerpo);
    const cambiar = (c: Partial<Borrador>) => { setB((x) => ({ ...x, ...c })); };

    const insertar = (k: string) => {
        const marca = `{{${k}}}`;
        if ((b.cuerpo + marca).length > 1024) {
            return;
        }
        const campo = document.getElementById('mt-cuerpo') as HTMLTextAreaElement | null;
        const pos = campo !== null && typeof campo.selectionStart === 'number' ? campo.selectionStart : b.cuerpo.length;
        cambiar({ cuerpo: b.cuerpo.slice(0, pos) + marca + b.cuerpo.slice(pos), ejemplos: b.ejemplos[k] !== undefined ? b.ejemplos : { ...b.ejemplos, [k]: k === 'enlace' ? enlace : (VARIABLES.find((v) => v.clave === k)?.ejemplo ?? '') } });
    };

    const enviar = () => {
        if (Object.keys(erroresPlantilla(b, existentes)).length > 0) {
            setIntentado(true);
            return;
        }
        onEnviar(b);
    };
    const ejemplosVista = Object.fromEntries(usadas.map((k) => [k, b.ejemplos[k] ?? nombreVariable(k)]));

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            titulo={b.nombre.trim() === '' ? 'Nueva plantilla' : b.nombre.trim()}
            eyebrow={corrige === null ? 'Nueva plantilla de Meta' : 'Corregir una plantilla'}
            subtitulo="Marketing · se envía a aprobación de Meta"
            icono={corrige === null ? 'plus' : 'pencil'}
            accion="Enviar a Meta"
            accionIcono="send"
            onAccion={enviar}
        >
            <div className="rv-cn-editor">
                {corrige !== null && (
                    <div className="rv-cn-alerta rv-cn-alerta--roja" role="note">
                        <Icono nombre="circle-x" />
                        <span>
                            <strong>{`Meta rechazó «${corrige.name}»`}</strong>
                            <span>{corrige.status_reason ?? ''}</span>
                        </span>
                    </div>
                )}
                {numeros.length > 1 && <Desplegable etiqueta="Número de WhatsApp" valor={b.canal} opciones={numeros} ayuda="La plantilla queda en la cuenta de ese número." onChange={(v) => { cambiar({ canal: v }); }} nombre="plantilla-numero" />}
                <Campo etiqueta="Nombre en Meta" valor={b.nombre} max={512} error={errores.nombre ?? null} ayuda="Minúsculas, números y _. No se cambia después." mayusculas="none" autocompletar="off" onChange={(v) => { cambiar({ nombre: v.toLowerCase().replace(/\s+/g, '_') }); }} nombre="plantilla-nombre" />
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Idioma</p>
                    <Segmentos etiqueta="Idioma" items={IDIOMAS.map((i) => ({ etiqueta: i.texto, icono: 'languages' as const }))} valor={Math.max(0, IDIOMAS.findIndex((i) => i.valor === b.idioma))} onCambio={(i) => { cambiar({ idioma: IDIOMAS[i]?.valor ?? 'es-ES' }); }} />
                    <span className="rv-cn-gris">Una plantilla por idioma. Para otro idioma, crea otra.</span>
                </div>
                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <p className="rv-ca-ceja">Categoría</p>
                        <span>Meta puede cambiarla al revisarla. Decide el precio del mensaje.</span>
                    </div>
                    <div role="radiogroup" aria-label="Categoría" className="rv-cn-categorias">
                        {[
                            ['marketing', 'Marketing', 'Promociones, eventos y novedades. Solo a quien aceptó promociones.'],
                            ['utility', 'Utilidad', 'Confirmaciones y avisos: las envía Reservaria por módulo y se cambian en Mensajes.'],
                            ['authentication', 'Autenticación', 'Códigos de acceso: los prepara Reservaria.'],
                        ].map(([k, t, d]) => (
                            <button key={k} type="button" role="radio" aria-checked={k === 'marketing'} aria-disabled={k !== 'marketing'} className={`rv-cn-categoria${k === 'marketing' ? ' rv-cn-categoria--on' : ''}`} disabled={k !== 'marketing'}>
                                <span className="rv-cn-categoria__punto" />
                                <span>
                                    <strong>{t}</strong>
                                    <small>{d}</small>
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Texto</p>
                    {b.conTitulo ? (
                        <Campo etiqueta="Título" valor={b.titulo} max={60} error={errores.titulo ?? null} ayuda="Opcional. Va en negrita, arriba. Sin variables." onChange={(v) => { cambiar({ titulo: v }); }} nombre="plantilla-titulo" />
                    ) : (
                        <button type="button" className="rv-wb-enlace" onClick={() => { cambiar({ conTitulo: true }); }}>
                            <Icono nombre="plus" />
                            <span>Añadir un título</span>
                        </button>
                    )}
                    <Campo id="mt-cuerpo" etiqueta="Mensaje" valor={b.cuerpo} filas={6} max={1024} error={errores.cuerpo ?? null} ayuda={`${b.cuerpo.length.toLocaleString('es-ES')} de 1.024. Las variables se rellenan en cada envío.`} mayusculas="sentences" onChange={(v) => { cambiar({ cuerpo: v }); }} />
                    <div className="rv-cn-insertar">
                        <span>Insertar</span>
                        {VARIABLES.map((v) => (
                            <button key={v.clave} type="button" className="rv-cn-variable" aria-label={`Insertar la variable ${v.nombre}`} onClick={() => { insertar(v.clave); }}>
                                <Icono nombre="braces" />
                                <span>{v.nombre}</span>
                            </button>
                        ))}
                        {/* G754: inserta una variable libre (`free`) y su ejemplo se pide en «Ejemplos para Meta». */}
                        <button type="button" className="rv-cn-variable rv-cn-variable--libre" aria-label="Insertar un texto libre" onClick={() => { insertar(siguienteLibre(b.cuerpo)); }}>
                            <Icono nombre="type" />
                            <span>Texto libre</span>
                        </button>
                    </div>
                    {b.conPie ? (
                        <Campo etiqueta="Pie" valor={b.pie} max={60} ayuda="Opcional. Texto pequeño, abajo." onChange={(v) => { cambiar({ pie: v }); }} nombre="plantilla-pie" />
                    ) : (
                        <button type="button" className="rv-wb-enlace" onClick={() => { cambiar({ conPie: true }); }}>
                            <Icono nombre="plus" />
                            <span>Añadir un pie</span>
                        </button>
                    )}
                </div>
                {libres.length > 0 && (
                    <div className="rv-ca-bloque">
                        <div className="rv-ca-bloque__cab">
                            <p className="rv-ca-ceja">Ejemplos para Meta</p>
                            <span>{`Meta pide un ejemplo de cada variable libre para revisarla. ${AVISO_EJEMPLO}`}</span>
                        </div>
                        {libres.map((k) => (
                            <Campo key={k} etiqueta={`Ejemplo de «${nombreVariable(k)}»`} valor={b.ejemplos[k] ?? ''} max={200} error={errores[`ejemplo_${k}`] ?? null} onChange={(v) => { cambiar({ ejemplos: { ...b.ejemplos, [k]: v } }); }} nombre={`ex-${k}`} />
                        ))}
                    </div>
                )}
                <div className="rv-ca-bloque">
                    <p className="rv-ca-ceja">Botones</p>
                    <Interruptor etiqueta={`«${BOTON_BAJA}»`} ayuda="Obligatorio en marketing: quien lo pulsa se da de baja." icono="user-x" valor bloqueado linea="arriba" />
                </div>
                <VistaPrevia titulo={b.conTitulo ? b.titulo.trim() : ''} cuerpo={b.cuerpo} pie={b.conPie ? b.pie.trim() : ''} botones={[BOTON_BAJA]} ejemplos={ejemplosVista} nombreNegocio={nombreNegocio} pie2="Con tus ejemplos" vacio="Escribe el mensaje para verlo aquí." />
                <p className="rv-ca-nota rv-ca-nota--gris">
                    <Icono nombre="lock" />
                    <span>Una vez enviada no se edita: para cambiar el texto se envía otra. Solo llega a quien aceptó recibir promociones.</span>
                </p>
            </div>
        </PanelLateral>
    );
}
